import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "./ui/input";
import { Edit, Plus, Save, Trash2, X, Repeat, Repeat1 } from "lucide-react";
import {
    Type, Rule, Code
} from "../shared/schema";
import { useAttributes, useDataTypes } from "@/hooks/useLocalStorage";

interface ExpressionRow {
    id: number;
    startGroup: string;
    operator: string;
    expressionType: "value" | "operand";
    expressionValue?: string;
    dataType?: string;
    attribute?: string;
    endGroup: string;
    saved?: boolean;
    addRootMode?: boolean; // true if user is adding value after ')'
    addRootValue?: string;
    addRootValueDT?: string;
    addRootValueAttr?: string;
    addRootType?: "value" | "operand";
}

interface ExpressionBuilderModalProps {
    open: boolean;
    onClose: () => void;
    initialExpression?: string;
    initialExpressionJson?: any[];
    onSave: (data: {
        finalExpression: string;
        expressionJson: {
            StartGroup: string;
            EndGroup: string;
            Operator: string;
            Value: string;
            DataType: string;
            Attribute: string;
            RootValue: string;
            RootDataType: string;
            RootAttribute: string;
        }[];
    }) => void;
}

export const ExpressionBuilderModal: React.FC<ExpressionBuilderModalProps> = ({
    open,
    onClose,
    initialExpression = "",
    initialExpressionJson = null,
    onSave,
}) => {
    const [rows, setRows] = useState<ExpressionRow[]>([
        {
            id: 1,
            startGroup: "",
            operator: "",
            expressionType: "value",
            expressionValue: "",
            dataType: "",
            attribute: "",
            endGroup: "",
            saved: false,
            addRootValue: "",
            addRootValueDT: "",
            addRootValueAttr: ""
        },

    ]);
    // console.log("exrows", rows)
    const [dataType, setDataType] = useState<(Type)[]>([]);
    const [attribute, setAttribute] = useState<(Code)[]>([]);
    const [attrCode, setAttrCode] = useState("");
    const [errorMsg, setErrorMsg] = useState("");

    const { data: existingDtValues } = useDataTypes(1, undefined, undefined);
    const { data: existingAttribute } = useAttributes(attrCode);

    useEffect(() => {
        if (existingDtValues && existingDtValues.total > 0) {
            setDataType(existingDtValues.items)
        }
    }, [existingDtValues])

    useEffect(() => {
        if (existingAttribute && existingAttribute.length > 0) {
            setAttribute(existingAttribute)
        } else {
            setAttribute([])
        }
    }, [existingAttribute])

    const parseInitialData = (jsonData: any[]) => {
        if (!jsonData || jsonData.length === 0) return null;

        return jsonData.map((item, index) => ({
            id: index,
            startGroup: item.StartGroup || "",
            operator: item.Operator || "",
            expressionType: item.Value ? "value" : "operand" as "value" | "operand",
            expressionValue: item.Value || "",
            dataType: item.DataType || "",
            attribute: item.Attribute || "",
            endGroup: item.EndGroup || "",
            saved: true,
            addRootMode: !!(item.RootValue || item.RootDataType),
            addRootValue: item.RootValue || "",
            addRootValueDT: item.RootDataType || "",
            addRootValueAttr: item.RootAttribute || "",
            addRootType: item.RootValue ? "value" : (item.RootDataType ? "operand" : "value") as "value" | "operand",
        }));
    };

    useEffect(() => {
        if (open && initialExpressionJson && initialExpressionJson.length > 0) {
            const parsedRows = parseInitialData(initialExpressionJson);
            if (parsedRows) {
                setRows(parsedRows);
            }
        } else if (open && !initialExpressionJson) {
            setRows([{
                id: 1,
                startGroup: "",
                operator: "",
                expressionType: "value",
                expressionValue: "",
                dataType: "",
                attribute: "",
                endGroup: "",
                saved: false,
                addRootValue: "",
                addRootValueDT: "",
                addRootValueAttr: ""
            }]);
        }
    }, [open, initialExpressionJson]);

    const handleRowChange = (id: number, key: keyof ExpressionRow, value: any) => {
        setRows((prev) =>
            prev.map((r) => {
                if (r.id === id) {
                    if (key === "addRootMode") {
                        return {
                            ...r,
                            [key]: value?.Name ? value.Name : value,
                            addRootValue: "",
                            addRootValueDT: "",
                            addRootValueAttr: "",
                        };
                    }

                    return { ...r, [key]: value?.Name ? value.Name : value };
                }
                return r;
            })
        );
        if (key === "dataType" || key === "addRootValueDT") {
            setAttrCode(value?.ID)
        }
    };

    const addRow = () => {
        setRows((prev) => [
            ...prev,
            {
                id: Date.now(),
                startGroup: "",
                operator: "",
                expressionType: "value",
                expressionValue: "",
                endGroup: "",
            },
        ]);
    };

    const deleteRow = (id: number) => {
        setRows((prev) => prev.filter((r) => r.id !== id));
    };

    const saveRow = (id: number) => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, saved: true } : r)));
    };

    const editRow = (id: number) => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, saved: false } : r)));
    };

    const toggleExpressionType = (id: number, isRoot = false) => {
        setRows((prev) =>
            prev.map((r) => {
                if (r.id !== id) return r;
                if (isRoot) {
                    return {
                        ...r,
                        addRootType: r.addRootType === "value" ? "operand" : "value",
                        addRootValue: "",
                        addRootValueDT: "",
                        addRootValueAttr: "",
                    };
                } else {
                    return {
                        ...r,
                        expressionType: r.expressionType === "value" ? "operand" : "value",
                        expressionValue: "",
                        dataType: "",
                        attribute: "",
                    };
                }
            })
        );
    };

    // build expression string
    const buildFinalExpression = () => {
        return rows
            .map((r) => {
                const expr =
                    r.expressionType === "value"
                        ? r.expressionValue
                        : `${r.dataType || ""}.${r.attribute || ""}`;
                const rootExpr =
                    r.addRootMode &&
                    (r.addRootType === "value"
                        ? r.addRootValue
                        : `${r.addRootValueDT || ""}.${r.addRootValueAttr || ""}`);
                return `${r.startGroup || ""} ${r.operator || ""} ${expr || ""} ${r.endGroup || ""
                    } ${rootExpr ? `r${rootExpr}` : ""}`;
            })
            .join(" ");
    };

    const validateExpression = (expr: string): string | null => {
        const stack: string[] = [];
        for (const ch of expr) {
            if (ch === "(") stack.push(ch);
            else if (ch === ")") {
                if (!stack.length) return "Unbalanced parentheses";
                stack.pop();
            }
        }
        if (stack.length) return "Unbalanced parentheses";

        if (/[\+\-\*\/%]{2,}/.test(expr)) return "Invalid consecutive operators";
        if (/^[\+\*\/%]/.test(expr.trim())) return "Expression cannot start with an operator";
        if (/[\+\-\*\/%]\s*$/.test(expr.trim()))
            return "Expression cannot end with an operator";

        return null;
    };

    const handleSaveAll = () => {
        const finalExpression = buildFinalExpression().replace(/\s+/g, " ").trim();
        const error = validateExpression(finalExpression);
        if (error) {
            setErrorMsg(error);
            return;
        }
        setErrorMsg("");

        const expressionJson = rows.map((r) => ({
            StartGroup: r.startGroup || "",
            EndGroup: r.endGroup || "",
            Operator: r.operator || "",
            Value: r.expressionType === "value" ? r.expressionValue || "" : "",
            DataType: r.expressionType === "operand" ? r.dataType || "" : "",
            Attribute: r.expressionType === "operand" ? r.attribute || "" : "",
            RootValue: r.addRootType === "value" ? r.addRootValue || "" : "",
            RootDataType: r.addRootType === "operand" ? r.addRootValueDT || "" : "",
            RootAttribute: r.addRootType === "operand" ? r.addRootValueAttr || "" : "",
        }));

        onSave({
            finalExpression: finalExpression.trim(),
            expressionJson,
        });

        setRows([{
            id: Date.now(),
            startGroup: "",
            operator: "",
            expressionType: "value",
            expressionValue: "",
            dataType: "",
            attribute: "",
            endGroup: "",
            saved: false,
            addRootValue: "",
            addRootValueDT: "",
            addRootValueAttr: ""
        }]);

        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent maxWidth="w-auto max-w-fit" minWidth="min-w-[800px]">
                <DialogHeader>
                    <DialogTitle>Expression Builder</DialogTitle>
                </DialogHeader>

                {/* Expression Table */}
                <div className="border rounded-md">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-muted">
                                <TableHead>Start Group</TableHead>
                                <TableHead>Operator</TableHead>
                                <TableHead>Expression</TableHead>
                                <TableHead>End Group</TableHead>
                                <TableHead>Action</TableHead>
                            </TableRow>
                        </TableHeader>

                        <TableBody>
                            {rows.map((row, idx) => (
                                <TableRow key={row.id}>
                                    {/* Start Group */}
                                    {!row.saved ? (
                                        <>
                                            <TableCell>
                                                <select
                                                    value={row.startGroup}
                                                    onChange={(e) =>
                                                        handleRowChange(row.id, "startGroup", e.target.value)
                                                    }
                                                    className="border rounded px-2 py-1 w-full"
                                                >
                                                    <option value="">None</option>
                                                    <option value="(">(</option>
                                                </select>
                                            </TableCell>

                                            {/* Operator */}
                                            <TableCell>
                                                <select
                                                    value={row.operator}
                                                    onChange={(e) =>
                                                        handleRowChange(row.id, "operator", e.target.value)
                                                    }
                                                    className="border rounded px-2 py-1 w-full"
                                                >
                                                    <option value="">Select</option>
                                                    <option value="+">+</option>
                                                    <option value="-">-</option>
                                                    <option value="*">*</option>
                                                    <option value="/">/</option>
                                                    <option value="%">%</option>
                                                </select>
                                            </TableCell>

                                            {/* Expression */}
                                            <TableCell>
                                                {row.expressionType === "value" ? (
                                                    <Input
                                                        value={row.expressionValue}
                                                        onChange={(e) =>
                                                            handleRowChange(row.id, "expressionValue", e.target.value)
                                                        }
                                                        placeholder="Enter value"
                                                    />
                                                ) : (
                                                    <div className="flex gap-2">
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="outline">
                                                                    {dataType.length > 0 ? !row.dataType ? "Select" : row.dataType : "Fetching DataType..."}
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent>
                                                                {dataType.map((opt) => (
                                                                    <DropdownMenuItem
                                                                        key={opt.Name}
                                                                        onClick={() =>
                                                                            handleRowChange(row.id, "dataType", opt)
                                                                        }
                                                                    >
                                                                        {opt.Name}
                                                                    </DropdownMenuItem>
                                                                ))}
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>

                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="outline" disabled={!row.dataType || attribute.length === 0}>
                                                                    {row.dataType ? attribute.length === 0 ? "No Attribute" : !row.attribute ? "Select" : row.attribute : "Select"}
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent>
                                                                {attribute.map((opt) => (
                                                                    <DropdownMenuItem
                                                                        key={opt.Name}
                                                                        onClick={() =>
                                                                            handleRowChange(row.id, "attribute", opt)
                                                                        }
                                                                    >
                                                                        {opt.Name}
                                                                    </DropdownMenuItem>
                                                                ))}
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </div>
                                                )}
                                            </TableCell>

                                            {/* End Group */}
                                            <TableCell>
                                                <div className="flex flex-row gap-1">
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="outline" className="w-[60px] text-left text-sm mr-2">
                                                                {row.endGroup || "Select "}
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent>
                                                            {[{ Name: "None", value: "" }, { Name: ")", value: ")" }].map((opt) => (
                                                                <DropdownMenuItem
                                                                    key={opt.Name}
                                                                    onClick={() => {
                                                                        const val = opt.value;
                                                                        handleRowChange(row.id, "endGroup", val);
                                                                        if (val === ")")
                                                                            handleRowChange(row.id, "addRootMode", true);
                                                                        else handleRowChange(row.id, "addRootMode", false);
                                                                    }}
                                                                >
                                                                    {opt.Name}
                                                                </DropdownMenuItem>
                                                            ))}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>

                                                    {/* Add Root Mode */}
                                                    {row.addRootMode && (
                                                        <>
                                                            {row.addRootType === "value" ? (
                                                                <Input
                                                                    value={row.addRootValue || ""}
                                                                    onChange={(e) =>
                                                                        handleRowChange(row.id, "addRootValue", e.target.value)
                                                                    }
                                                                />
                                                            ) : (
                                                                <div className="flex gap-2">
                                                                    <DropdownMenu>
                                                                        <DropdownMenuTrigger asChild>
                                                                            <Button variant="outline">
                                                                                {dataType.length > 0 ? !row.addRootValueDT ? "Select" : row.addRootValueDT : "Fetching DataType..."}
                                                                            </Button>
                                                                        </DropdownMenuTrigger>
                                                                        <DropdownMenuContent>
                                                                            {dataType.map((opt) => (
                                                                                <DropdownMenuItem
                                                                                    key={opt.Name}
                                                                                    onClick={() =>
                                                                                        handleRowChange(row.id, "addRootValueDT", opt)
                                                                                    }
                                                                                >
                                                                                    {opt.Name}
                                                                                </DropdownMenuItem>
                                                                            ))}
                                                                        </DropdownMenuContent>
                                                                    </DropdownMenu>

                                                                    <DropdownMenu>
                                                                        <DropdownMenuTrigger asChild>
                                                                            <Button variant="outline" disabled={!row.addRootValueDT || attribute.length === 0}>
                                                                                {row.addRootValueDT ? attribute.length === 0 ? "No Attribute" : !row.addRootValueAttr ? "Select" : row.addRootValueAttr : "Select"}
                                                                            </Button>
                                                                        </DropdownMenuTrigger>
                                                                        <DropdownMenuContent>
                                                                            {attribute.map((opt) => (
                                                                                <DropdownMenuItem
                                                                                    key={opt.Name}
                                                                                    onClick={() =>
                                                                                        handleRowChange(row.id, "addRootValueAttr", opt)
                                                                                    }
                                                                                >
                                                                                    {opt.Name}
                                                                                </DropdownMenuItem>
                                                                            ))}
                                                                        </DropdownMenuContent>
                                                                    </DropdownMenu>
                                                                </div>
                                                            )}

                                                            <div className="flex gap-1 mt-1">
                                                                <Button
                                                                    size="sm"
                                                                    variant="link"
                                                                    tooltip={row.addRootType === "value" ? "Operand" : "Value"}
                                                                    onClick={() => toggleExpressionType(row.id, true)}
                                                                >
                                                                    <Repeat1 className="h-4 w-4 text-gray-700" />
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="link"
                                                                    tooltip="Cancel"
                                                                    onClick={() =>
                                                                        handleRowChange(row.id, "addRootMode", false)
                                                                    }
                                                                >
                                                                    <X className="h-4 w-4 text-red-500" />
                                                                </Button>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell>
                                                <div className="flex ">
                                                    <Button
                                                        size="sm"
                                                        variant="link"
                                                        tooltip={row.expressionType === "value" ? "Operand" : "Value"}
                                                        onClick={() => toggleExpressionType(row.id)}
                                                    >
                                                        <Repeat1 className="h-4 w-4 text-gray-700" />
                                                    </Button>

                                                    <div className="flex gap-1">
                                                        <Button tooltip="Save" variant="link" size="sm" onClick={() => saveRow(row.id)}>
                                                            <Save className="h-4 w-4 text-gray-700" />
                                                        </Button>
                                                        {idx > 0 && (
                                                            <Button
                                                                size="sm"
                                                                variant="link"
                                                                tooltip="Cancel"
                                                                onClick={() => deleteRow(row.id)}
                                                            >
                                                                <X className="h-4 w-4 text-gray-700" />
                                                            </Button>
                                                        )}
                                                        <Button tooltip="Add Row" variant="link" size="sm" onClick={addRow}>
                                                            <Plus className="h-4 w-4 text-gray-700" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </TableCell>
                                        </>
                                    ) : (
                                        <>
                                            <>
                                                <TableCell className="border p-1 text-sm">{row.startGroup}</TableCell>
                                                <TableCell className="border p-1 text-sm">{row.operator}</TableCell>
                                                <TableCell className="border p-1 text-sm">{row.expressionType === "value" ? `${row.expressionValue}` : row.dataType && `${row.dataType}${"." + row.attribute}`}</TableCell>
                                                <TableCell className="border p-1 text-sm">{row.endGroup}{row.addRootType === "value" ? row.addRootValue && `r${row.addRootValue}` : row?.addRootValueDT && `r${row?.addRootValueDT}${row?.addRootValueAttr && "." + row?.addRootValueAttr}`}</TableCell>
                                                <TableCell className="border p-1 text-sm">
                                                    <Button tooltip="Edit" variant="link" size="sm" onClick={() => editRow(row.id)}>
                                                        <Edit className="h-4 w-4 text-gray-700" />
                                                    </Button>
                                                    {idx > 0 && (
                                                        <Button
                                                            size="sm"
                                                            variant="link"
                                                            tooltip="Delete Row"
                                                            onClick={() => deleteRow(row.id)}
                                                        >
                                                            <Trash2 className="h-4 w-4 text-red-500" />
                                                        </Button>
                                                    )}
                                                    <Button tooltip="Add Row" variant="link" size="sm" onClick={addRow}>
                                                        <Plus className="h-4 w-4 text-gray-700" />
                                                    </Button>
                                                </TableCell>
                                            </>
                                        </>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
                {errorMsg && (
                    <p className="text-red-500 text-sm mt-2 text-center">{errorMsg}</p>
                )}
                <DialogFooter className="mt-4">
                    <Button onClick={handleSaveAll}>Save Expression</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
